import React, { Component } from 'react'
import classnames from 'classnames'
import { observer, inject } from 'mobx-react'

import { withSettings } from '../withSettings'
import Form from '../Form'
import { number } from '../Form/validators'
import TypesRestrictions from './TypesRestrictions'
import LocationRestrictions from './LocationRestrictions'
import PremiumLabel from '../Form/PremiumLabel'
import SearchWidget from '../../../../SearchWidget'
import Layout from './Layout'
import SortMenu from '../../../../util/SortMenu'
import { isPremium } from '../../../../../utils/plans'

@withSettings
@inject(['websites'])
@observer
export default class SearchSettings extends Component {
  website = this.props.websites.current

  state = {
    defaultSearchFilters: null,
    streamlined: this.props.getSettingsAt('search.streamlined', false),
  }

  handleData = data => {
    const { defaultSearchFilters, streamlined } = this.state
    const { sort_by, sort_dir, restrictions, results_type, display_side_map } = data
    
    const settings = { ...this.props.websites.current.settings }
    if (!settings.search) settings.search = {}
    
    const filters = { ...defaultSearchFilters, sort_by, sort_dir }
    if (sort_by === 'map_center') {
      filters.sort_by = null
    }

    settings.search.default_filters = filters
    settings.search.display_side_map = display_side_map == 1
    settings.search.streamlined = !!streamlined

    if (display_side_map == 1) {
      settings.search.results_type = 'card'
    } else if (results_type) {
      settings.search.results_type = results_type
    } else {
      // Fallback to existing or default
      settings.search.results_type = settings.search.results_type || 'card'
    }

    settings.search.restrictions = {
      ...restrictions,
      sale_type: this.saleType.getValues(),
      listing_type: this.listingType.getValues(),
      property_type: this.propertyType.getValues(),
      status: this.statusType.getValues(),
      location: {
        restrictions: this.locationRestrictions.getValues(),
        // exclusions: this.locationExclusions.getValues()
      }
    }

    return this.props.websites.save({ settings })
  }

  handleStreamlined = event => {
    event.preventDefault()
    this.setState({ streamlined: true })
  }

  handleAdvanced = event => {
    event.preventDefault()
    this.setState({ streamlined: false })
  }

  render() {
    const { websites: { current: website } } = this.props

    const search = this.props.getSettingsAt('search', {
      streamlined: false,
      results_type: 'card',
      default_filters: {},
      restrictions: {
        sale_type: [],
        listing_type: [],
        property_type: [],
        status: [],
        location: {
          restrictions: [],
          exclusions: []
        }
      }
    })

    const validations = [
      { field: 'restrictions[min_price]', validate: number, message: 'The minimum sale price must be a number' },
      { field: 'restrictions[max_price]', validate: number, message: 'The maximum sale price must be a number' }
    ]

    const { sort_by, sort_dir } = search.default_filters
    const { results_type } = search

    const { streamlined } = this.state

    return (
      <div className="container padded settings">
        <Form onData={this.handleData} validations={validations}>
          
          <div className="panel padded">
            <h2 className="title">Consumer-Facing Search Interface</h2>
            <p className="muted">
              Choose which style of search form that you want the users of your website to use...
            </p>
            <p><strong>Note: The advanced search cannot be used with the left side map view. If you have the left side map view enabled, choosing the advanced search option will reset your map view setting.</strong></p>
            <div className="search_ui_toggle">
              <div onClick={this.handleStreamlined}>
                <h3>
                  <input type="radio" name="search_ui" value="streamlined" checked={streamlined} />
                  Streamlined Search Form
                </h3>
                <ul>
                  <li>Ideal For Searching Multiple MLS's</li>
                  <li>Recommended</li>
                </ul>
              </div>
              <div onClick={this.handleAdvanced}>
                <h3>
                  <input type="radio" name="search_ui" value="advanced" checked={!streamlined} />
                  Advanced Search Form
                </h3>
                <ul>
                  <li>Detailed Advanced Features</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="panel padded">
            <h2 className="title">Default Search Appearance</h2>

            <Layout advancedSearchActive={!this.state.streamlined} website={website} searchConfig={search} />

            <p className="muted">
              Adjust how the search filters will be set when loading the default search page.
            </p>

            <SearchWidget website_uuid={website.uuid} applyAgent onChange={filters => this.setState({
              defaultSearchFilters: filters
            })} advanced={!streamlined} streamlined={streamlined} />

            <div className="form-section">
              <label>Results Sorting</label>
              <p className="muted">This will re-order the results on hotsheets and recommendations.</p>
              Sort By:
              <SortMenu sortBy={sort_by} sortDir={sort_dir} saleType={this.state.defaultSearchFilters && this.state.defaultSearchFilters.sale_type} />

              {!search.display_side_map && (
                <span>
                  Results Type:
                  <select
                    name="results_type"
                    defaultValue={results_type || 'card'}
                    style={{ marginLeft: 5, marginRight: 15 }}
                  >
                    <option value="card">Card (Grid)</option>
                    <option value="list">List</option>
                  </select>
                </span>
              )}
            </div>
          </div>

          <div className="panel padded">
            <h2 className="title">Search Restrictions</h2>

            <div className="form-section">
              <label>Location Filtering</label>
              <p className="muted">
                This allows you to restrict your search to only show certain areas and to exclude areas from search results.
              </p>
              <div className="restriction-set">
                <div className="location-restrictions">
                  <label>Allow Search Only In These Locations</label>
                  <LocationRestrictions
                    ref={el => this.locationRestrictions = el}
                    defaultValues={search.restrictions.location.restrictions}
                    websiteUuid={website.uuid} />
                </div>
                {/*Hiding this section until Cirrus fixes*/}
                {false && (<div className={classnames('location-exclusions', {disabled: website.plan !== 'premium'})}>
                  <label>Exclude Locations <PremiumLabel website={website}/></label>
                  <LocationRestrictions
                      ref={el => this.locationExclusions = el}
                      defaultValues={search.restrictions.location.exclusions}
                      disabled={!isPremium(website.plan)}
                      websiteUuid={website.uuid}/>
                </div>)}
              </div>
            </div>

            <div className="form-section">
              <label>Sale Price Filter</label>
              <p className="muted">
                Set the minimum and maximum price of listings that will show in the search.
              </p>
              <div className="restriction-set">
                <div>
                  <input
                    placeholder="Set a Minimum Price"
                    name="restrictions[min_price]"
                    defaultValue={search.restrictions.min_price} />
                  <p className="muted">
                    No listings will show under this amount
                  </p>
                </div>
                <div>
                  <input
                    placeholder="Set a Maximim Price"
                    name="restrictions[max_price]"
                    defaultValue={search.restrictions.max_price} />
                  <p className="muted">
                    No listings will show over this amount
                  </p>
                </div>
              </div>
            </div>

            <div className="form-section">
              <label>Sale Type Filter</label>
              <p className="muted">
                Set which sale types you want to appear in the search.
              </p>
              <TypesRestrictions
                ref={el => this.saleType = el}
                values={website.data.types.sale}
                selected={search.restrictions.sale_type} />
            </div>

            <div className="form-section">
              <label>Listing Type Filter</label>
              <p className="muted">
                Set which listing types you want to appear in the search.
              </p>
              <TypesRestrictions
                ref={el => this.listingType = el}
                values={website.data.types.listing}
                selected={search.restrictions.listing_type} />
            </div>

            <div className="form-section">
              <label>Property Type Filter</label>
              <p className="muted">
                Set which property types you want to appear in the search.
              </p>
              <TypesRestrictions
                ref={el => this.propertyType = el}
                values={website.data.types.property}
                selected={search.restrictions.property_type} />
            </div>

            <div className="form-section">
              <label>Status Filter</label>
              <p className="muted">
                Set which statuses you want to appear in the search.
              </p>
              <TypesRestrictions
                ref={el => this.statusType = el}
                values={website.data.statuses}
                selected={search.restrictions.status} />
            </div>
          </div>

          <button type="submit" className="big shadowed">Save Changes</button>
        </Form>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// client/components/sections/website/settings/Search/index.js